import {
  Badge,
  BadgeProps,
  Box,
  Button,
  ButtonGroup,
  ButtonProps,
  Divider,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  List,
  ListItem,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverFooter,
  PopoverTrigger,
  Spinner,
  Text,
  useColorModeValue,
  useDisclosure,
  useFormControl,
  useOutsideClick,
} from "@chakra-ui/react";
import { RiArrowDownSLine, RiCloseLine } from "@remixicon/react";
import {
  ForwardedRef,
  forwardRef,
  ReactNode,
  useMemo,
  useRef,
  useState,
} from "react";

import AnimatedSpinner from "@/common/components/AnimatedSpinner";
import { IcoSearch } from "@/common/components/Icons";
import Mark from "@/common/components/Mark";
import MenuDescription from "@/common/components/MenuDescription";
import { PortalWrapper } from "@/common/components/PortalWrapper";
import { useDebounce } from "@/common/hooks/useDebounce";
import useInputWithSelectableListItems from "@/common/hooks/useInputWithSelectableListItems";
import { AutocompleteValueProps } from "@/common/utils/autocomplete";
import { fuzzyMatch } from "@reflag/shared/utils/stringSimilarity";

export type EmphasizedSuggestion = {
  key: string;
  badge?: BadgeProps;
};

export function getStringRenderFunction<
  TSuggestion extends Record<string, string | number>,
>(key: keyof TSuggestion) {
  return (item: TSuggestion, search?: string): ReactNode => {
    const displayValue = item[key];

    if (search) {
      return highlightStringMatch(displayValue, search);
    }

    return displayValue;
  };
}

export function getStringFilterFunction<TSuggestion extends object>(
  key: keyof TSuggestion,
) {
  return (item: TSuggestion, search: string): boolean => {
    return String(item[key])
      .toLocaleLowerCase()
      .includes(search.toLocaleLowerCase());
  };
}

export function highlightStringMatch(
  value: string | number,
  search: string,
): ReactNode {
  const stringValue = String(value);
  const parts = fuzzyMatch(stringValue, search);

  return parts.map(({ piece, match }) => {
    if (match) {
      return <Mark key={`mark-${piece}`}>{piece}</Mark>;
    }

    return piece;
  });
}

export type AutocompleteSelectProps<TSuggestion extends Record<string, any>> =
  Omit<ButtonProps, "value" | "onChange" | "onBlur"> & {
    suggestions: TSuggestion[];
    itemKeyProperty: keyof TSuggestion;
    value?: TSuggestion | null;
    maxSuggestions?: number;
    placeholder?: ReactNode;
    description?: ReactNode;
    emphasized?: EmphasizedSuggestion[];
    closeOnSelect?: boolean;
    isLoadingSuggestions?: boolean;
    usePortal?: boolean;
    showArrow?: boolean;
    footer?: ReactNode;
    hasMore?: boolean;
    isLoadingMore?: boolean;
    itemRenderFn: (suggestion: TSuggestion, search?: string) => ReactNode;
    /** Define this function for client side filtering */
    itemFilterFn?: (
      suggestion: TSuggestion,
      search: string,
      suggestions: TSuggestion[],
    ) => boolean;
    onSearchInput?: (search: string) => void;
    onBlur?: () => void;
    onClear?: () => void;
    onLoadMore?: () => void;
  } & AutocompleteValueProps<TSuggestion>;

function AutocompleteSelectInner<TSuggestion extends Record<string, any>>(
  {
    suggestions,
    itemKeyProperty,
    value,
    maxSuggestions = Infinity,
    placeholder = "",
    size = "sm",
    description,
    emphasized = [],
    closeOnSelect = true,
    isLoadingSuggestions = false,
    usePortal,
    showArrow = false,
    canClear,
    footer,
    hasMore,
    isLoadingMore,
    itemRenderFn,
    itemFilterFn = () => true,
    onSearchInput,
    onChange,
    onBlur,
    onClear,
    onLoadMore,
    ...rest
  }: AutocompleteSelectProps<TSuggestion>,
  ref: ForwardedRef<HTMLButtonElement>,
) {
  const { isOpen, onClose, onOpen, onToggle } = useDisclosure();
  const formControlProps = useFormControl<HTMLButtonElement>(rest);
  const initialFocusRef = useRef(null);

  const bgHoverColor = useColorModeValue("gray.50", "gray.700");

  const [searchValue, setSearchValue] = useState("");
  const debouncedIsLoadingSuggestions = useDebounce(isLoadingSuggestions, 200);

  const cappedSuggestions = useMemo(() => {
    const filteredSuggestions = suggestions.filter((s) =>
      itemFilterFn(s, searchValue, suggestions),
    );
    return filteredSuggestions
      .sort(
        (a, b) =>
          emphasized.findIndex(({ key }) => key === b[itemKeyProperty]) -
          emphasized.findIndex(({ key }) => key === a[itemKeyProperty]),
      )
      .slice(0, maxSuggestions);
  }, [
    suggestions,
    maxSuggestions,
    itemFilterFn,
    searchValue,
    itemKeyProperty,
    emphasized,
  ]);

  const { selected, onKeyDown } = useInputWithSelectableListItems({
    isOpen: isOpen,
    initial: value
      ? (suggestions.findIndex((s) => s === value) ?? undefined)
      : undefined,
    maxItems: cappedSuggestions.length,
    hasExactMatch: false, // always false since we want to be able to pick exact matches
    onPickSelected: (selected) => {
      onChange?.(cappedSuggestions[selected]);
      onClose();
    },
  });

  const outsideRef = useRef(null);
  useOutsideClick({
    ref: outsideRef,
    handler: () => onClose(),
  });

  const placeholderColor = useColorModeValue("gray.500", "gray.500");

  const {
    w,
    width,
    maxW,
    maxWidth,
    flex,
    flexGrow,
    flexShrink,
    ...buttonProps
  } = rest;
  const sizingProps = {
    w,
    width,
    maxW,
    maxWidth,
    flex,
    flexGrow,
    flexShrink,
  };

  return (
    <Popover
      initialFocusRef={initialFocusRef}
      isOpen={isOpen}
      placement="bottom-start"
      returnFocusOnClose={true}
      trigger="click"
      onClose={() => onBlur?.()}
      onOpen={() => setSearchValue("")}
    >
      <Box ref={outsideRef} {...sizingProps} position="relative">
        <PopoverTrigger>
          <ButtonGroup w="full" isAttached>
            <Button
              {...formControlProps}
              ref={ref}
              color={!value ? placeholderColor : undefined}
              flex="1 1 auto"
              isDisabled={formControlProps.disabled}
              overflow="hidden"
              rightIcon={
                <Box fontSize="xl" mr={-2}>
                  <RiArrowDownSLine size={16} />
                </Box>
              }
              size={size}
              textOverflow="ellipsis"
              variant="input"
              onClick={() => onToggle()}
              onKeyDown={(e) => {
                if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                  e.preventDefault();
                  onOpen();
                }
              }}
              {...buttonProps}
            >
              <Box maxWidth="100%" overflow="hidden" width="100%">
                {value ? (
                  itemRenderFn(value)
                ) : (
                  <Text fontSize={size}>{placeholder}</Text>
                )}
              </Box>
            </Button>
            {!!value && canClear && (
              <IconButton
                aria-label="Clear selection"
                bg="appBackground"
                icon={<RiCloseLine size={16} />}
                isDisabled={formControlProps.disabled}
                marginInlineStart={"-1px"}
                size={size}
                variant="outline"
                onClick={() => {
                  onChange?.(null);
                  onClear?.();
                }}
              />
            )}
          </ButtonGroup>
        </PopoverTrigger>

        <PortalWrapper usePortal={usePortal}>
          <PopoverContent
            // workaround for to avoid horizontal scrollbar when positioned to the right of the screen
            rootProps={
              !usePortal
                ? { style: { transform: "scale(0)", width: "100%" } }
                : undefined
            }
            width="100%"
          >
            {showArrow && <PopoverArrow />}
            <PopoverBody p={0}>
              {description && (
                <>
                  <MenuDescription>{description}</MenuDescription>
                  <Divider borderColor="inherit" my={0} />
                </>
              )}
              <InputGroup size="sm" variant="flushed">
                <InputLeftElement mx={1} pointerEvents="none">
                  <IcoSearch color="dimmed" size="md" />
                </InputLeftElement>
                <InputRightElement mx={1} pointerEvents="none">
                  <AnimatedSpinner
                    show={debouncedIsLoadingSuggestions}
                    size="xs"
                    variant="subtle"
                  />
                </InputRightElement>
                <Input
                  ref={initialFocusRef}
                  borderTopRadius={!description ? "md" : undefined}
                  pl={10}
                  placeholder="Search"
                  value={searchValue}
                  onChange={(e) => {
                    const searchValue = e.target.value;
                    setSearchValue(searchValue);
                    onSearchInput?.(searchValue);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      onClose();
                    }
                    onKeyDown(e);
                  }}
                />
              </InputGroup>
              <List maxH="sm" overflowY="auto">
                {cappedSuggestions.length ? (
                  <>
                    {cappedSuggestions.map((item, index) => {
                      const badge = emphasized.find(
                        ({ key }) => key === item[itemKeyProperty],
                      )?.badge;
                      return (
                        <ListItem
                          key={item[itemKeyProperty]}
                          _hover={{ bg: bgHoverColor }}
                          alignItems="center"
                          bg={selected === index ? bgHoverColor : undefined}
                          cursor="pointer"
                          display="flex"
                          fontSize="sm"
                          px={3}
                          py={1}
                          onClick={() => {
                            setSearchValue("");
                            onChange?.(item);
                            if (closeOnSelect) onClose();
                          }}
                        >
                          {itemRenderFn(item, searchValue)}
                          {badge && <Badge ml="auto" {...badge} />}
                        </ListItem>
                      );
                    })}
                    {hasMore && (
                      <ListItem
                        _hover={{ bg: bgHoverColor }}
                        alignItems="center"
                        cursor="pointer"
                        display="flex"
                        fontSize="xs"
                        justifyContent="center"
                        px={3}
                        py={2}
                        onClick={(e) => {
                          e.preventDefault();
                          onLoadMore?.();
                        }}
                      >
                        {isLoadingMore ? (
                          <Spinner my={1} size="xs" variant="subtle" />
                        ) : (
                          <Text color="gray.500">Load more</Text>
                        )}
                      </ListItem>
                    )}
                  </>
                ) : (
                  <Text color="gray.500" fontSize="sm" px={3} py={1}>
                    {debouncedIsLoadingSuggestions
                      ? "Loading..."
                      : "Nothing found"}
                  </Text>
                )}
              </List>
            </PopoverBody>
            {footer && <PopoverFooter p={2}>{footer}</PopoverFooter>}
          </PopoverContent>
        </PortalWrapper>
      </Box>
    </Popover>
  );
}

const AutocompleteSelect = forwardRef(AutocompleteSelectInner) as (<
  TSuggestion extends Record<string, any>,
>(
  props: AutocompleteSelectProps<TSuggestion> & {
    ref?: ForwardedRef<HTMLButtonElement>;
  },
) => ReturnType<typeof AutocompleteSelectInner>) & { displayName: string };

AutocompleteSelect.displayName = "AutocompleteSelect";

export default AutocompleteSelect;
